import * as K from 'common/constants';
import { SortOrder } from 'common/graphqlTypes';

import { plugin } from 'plugins/deep-visibility';
import { DATA_SOURCE_CATEGORY_FIELD, DATA_SOURCE_NAME_FIELD, DATASOURCE, EVENT_CATEGORY_FIELD, schemas } from 'schemas';
import { View, VIEW_NAME } from '../types';
import { DATA_SOURCE_CATEGORY_VALUE } from './constants';

export const edrView: View = {
  name: VIEW_NAME.EDR,
  label: 'EDR',
  order: 2,
  dataSources: [DATASOURCE.SENTINEL_ONE],
  plugin: plugin.name,
  displaySettings: {
    columns: [
      'agent.uuid',
      'event.category',
      'event.type',
      'event.time',
      'src.process.uid',
      'src.process.storyline.id',
      'src.process.user',
      'osSrc.process.uid',
      'src.process.parent.uid',
      'event.details',
    ],
    disableLiveTail: true,
    displayMode: K.DISPLAY_MODE.TABLE,
    fontFamily: K.FONT_FAMILY.REGULAR,
    searchAllTeamsByDefault: true,
    showChart: true,
    sortOrder: SortOrder.MOST_RECENT_AT_TOP,
  },
  filter: `${DATA_SOURCE_CATEGORY_FIELD} = '${DATA_SOURCE_CATEGORY_VALUE}' ${DATA_SOURCE_NAME_FIELD} = '${
    schemas[DATASOURCE.SENTINEL_ONE].dataSourceId
  }'`,
  sources: {
    categoryField: DATA_SOURCE_CATEGORY_FIELD,
    categoryValue: DATA_SOURCE_CATEGORY_VALUE,
    sourceField: DATA_SOURCE_NAME_FIELD,
    sourceLabel: 'Source',
    sourceLabelPlural: 'Sources',
    sourceValues: [schemas[DATASOURCE.SENTINEL_ONE].dataSourceId], // should come from somewhere else
  },
  classNames: {
    tableField: 'DVTable__Cell',
    tableSeverityField: 'DVTable__SeverityField',
  },
  resultTabs: [
    {
      id: 'all',
      label: 'All Events',
    },
    {
      id: 'process',
      label: 'Processes',
      icon: 'processes_ic',
      filter: `${EVENT_CATEGORY_FIELD} = 'process'`,
      displaySettings: {
        columns: [
          'agent.uuid',
          'event.type',
          'event.time',
          'src.process.uid',
          'src.process.storyline.id',
          'src.process.cmdline',
          'src.process.user',
          'src.process.image.path',
          'src.process.uid',
          'src.process.integrityLevel',
          'src.process.signedStatus',
          'src.process.publisher',
          'osSrc.process.uid',
          'src.process.parent.uid',
          'tgt.process.uid',
          'tgt.process.cmdline',
          'tgt.process.displayName',
        ],
      },
    },
    {
      id: 'cross_process',
      label: 'Cross Processes',
      icon: 'cross-process',
      filter: `${EVENT_CATEGORY_FIELD} = 'cross_process'`,
      displaySettings: {
        columns: [
          'agent.uuid',
          'event.type',
          'event.time',
          'src.process.uid',
          'src.process.storyline.id',
          'src.process.user',
          'osSrc.process.uid',
          'src.process.parent.uid',
          'tgt.process.uid',
          'tgt.process.relation',
          'tgt.process.cmdline',
          'tgt.process.displayName',
        ],
      },
    },
    {
      id: 'indicators',
      label: 'Indicators',
      icon: 'indicators_ic',
      filter: `${EVENT_CATEGORY_FIELD} = 'indicators'`,
      displaySettings: {
        columns: [
          'agent.uuid',
          'event.type',
          'event.time',
          'src.process.uid',
          'src.process.storyline.id',
          'src.process.user',
          'osSrc.process.uid',
          'src.process.parent.uid',
          'indicator.name',
          'indicator.category',
          'indicator.description',
          'indicator.metadata',
        ],
      },
    },
    {
      id: 'threat_intelligence_indicators',
      label: 'Threat Intelligence Indicators',
      icon: 'forensics-detected-by',
      filter: `${EVENT_CATEGORY_FIELD} = 'threat_intelligence_indicators'`,
      displaySettings: {
        columns: [
          'agent.uuid',
          'event.type',
          'event.time',
          'src.process.uid',
          'src.process.storyline.id',
          'src.process.user',
          'osSrc.process.uid',
          'src.process.parent.uid',
          'tiIndicator.source',
          'tiIndicator.value',
          'tiIndicator.name',
          'tiIndicator.creationTime',
        ],
      },
    },
    {
      id: 'file',
      label: 'Files',
      icon: 'files_ic',
      filter: `${EVENT_CATEGORY_FIELD} = 'file'`,
      displaySettings: {
        columns: [
          'agent.uuid',
          'event.type',
          'event.time',
          'src.process.uid',
          'src.process.storyline.id',
          'src.process.user',
          'src.process.signedStatus',
          'src.process.publisher',
          'osSrc.process.uid',
          'src.process.parent.uid',
          'tgt.file.path',
          'tgt.file.type',
          'tgt.file.size',
        ],
      },
    },
    {
      id: 'ip',
      label: 'Network Actions',
      icon: 'mgmt-ranger_network',
      filter: `${EVENT_CATEGORY_FIELD} = 'ip'`,
      displaySettings: {
        columns: [
          'agent.uuid',
          'event.type',
          'event.time',
          'src.process.uid',
          'src.process.storyline.id',
          'src.process.user',
          'osSrc.process.uid',
          'src.process.parent.uid',
          'src.ip.address',
          'src.port.number',
          'dst.ip.address',
          'dst.port.number',
          'event.network.direction',
          'event.network.connectionStatus',
        ],
      },
    },
    {
      id: 'dns',
      label: 'DNS',
      icon: 'dns_ic',
      filter: `${EVENT_CATEGORY_FIELD} = 'dns'`,
      displaySettings: {
        columns: [
          'agent.uuid',
          'event.type',
          'event.time',
          'src.process.uid',
          'src.process.storyline.id',
          'src.process.user',
          'osSrc.process.uid',
          'src.process.parent.uid',
          'event.dns.request',
          'event.dns.response',
        ],
      },
    },
    {
      id: 'url',
      label: 'URL',
      icon: 'urls_ic',
      filter: `${EVENT_CATEGORY_FIELD} = 'url'`,
      displaySettings: {
        columns: [
          'agent.uuid',
          'event.type',
          'event.time',
          'src.process.uid',
          'src.process.storyline.id',
          'src.process.user',
          'osSrc.process.uid',
          'src.process.parent.uid',
          'url.address',
          'event.url.action',
        ],
      },
    },
    {
      id: 'registry',
      label: 'Registry',
      icon: 'registry_key_ic',
      filter: `${EVENT_CATEGORY_FIELD} = 'registry'`,
      displaySettings: {
        columns: [
          'agent.uuid',
          'event.type',
          'event.time',
          'src.process.uid',
          'src.process.storyline.id',
          'src.process.user',
          'osSrc.process.uid',
          'src.process.parent.uid',
          'registry.keyPath',
          'registry.keyUid',
          'registry.valueType',
          'registry.value',
          'registry.valueFullSize',
          'registry.valueIsComplete',
          'registry.oldValueType',
          'registry.oldValue',
          'registry.oldValueFullSize',
          'registry.oldValueIsComplete',
        ],
      },
    },
    {
      id: 'module',
      label: 'Modules',
      icon: 'dll_ic',
      filter: `${EVENT_CATEGORY_FIELD} = 'module'`,
      displaySettings: {
        columns: [
          'agent.uuid',
          'event.type',
          'event.time',
          'src.process.uid',
          'src.process.storyline.id',
          'src.process.user',
          'osSrc.process.uid',
          'src.process.parent.uid',
          'module.path',
          'module.sha1',
        ],
      },
    },
    {
      id: 'scheduled_task',
      label: 'Scheduled Tasks',
      icon: 'scheduled_tasks_ic',
      filter: `${EVENT_CATEGORY_FIELD} = 'scheduled_task'`,
      displaySettings: {
        columns: [
          'agent.uuid',
          'event.type',
          'event.time',
          'src.process.uid',
          'src.process.storyline.id',
          'src.process.user',
          'osSrc.process.uid',
          'src.process.parent.uid',
          'task.name',
          'task.path',
        ],
      },
    },
    {
      id: 'logins',
      label: 'Logins',
      icon: 'logins-ic',
      filter: `${EVENT_CATEGORY_FIELD} = 'logins'`,
      displaySettings: {
        columns: [
          'agent.uuid',
          'event.type',
          'event.time',
          'src.process.uid',
          'src.process.storyline.id',
          'src.process.user',
          'osSrc.process.uid',
          'src.process.parent.uid',
          'event.login.userName',
          'event.login.baseType',
          'src.endpoint.ip.address',
          'event.login.loginIsSuccessful',
          'event.login.type',
          'event.login.isAdministratorEquivalent',
          'event.login.failureReason',
        ],
      },
    },
    {
      id: 'command_script',
      label: 'Command Scripts',
      icon: 'command-scripts',
      filter: `${EVENT_CATEGORY_FIELD} = 'command_script'`,
      displaySettings: {
        columns: [
          'agent.uuid',
          'event.type',
          'event.time',
          'src.process.uid',
          'src.process.storyline.id',
          'src.process.cmdline',
          'src.process.user',
          'src.process.image.path',
          'cmdScript.content',
          'cmdScript.isComplete',
          'cmdScript.originalSize',
          'cmdScript.applicationName',
          'osSrc.process.uid',
          'src.process.parent.uid',
        ],
      },
    },
    {
      id: 'firmware',
      label: 'Firmware',
      icon: 'firmware',
      filter: `${EVENT_CATEGORY_FIELD} = 'firmware'`,
      displaySettings: {
        columns: ['agent.uuid', 'endpoint.os', 'event.type', 'event.time'],
      },
    },
    {
      id: 'named_pipe',
      label: 'Named Pipes',
      icon: 'named-pipes',
      filter: `${EVENT_CATEGORY_FIELD} = 'named_pipe'`,
      displaySettings: {
        columns: [
          'agent.uuid',
          'event.type',
          'event.time',
          'src.process.uid',
          'src.process.storyline.id',
          'src.process.user',
          'osSrc.process.uid',
          'src.process.parent.uid',
        ],
      },
    },
  ],
};
